import axios from "axios";
import { BACKEND_URL } from "../../config/api";
import {
  PaginatedPaymentsResponse,
  PaginationParams,
  PaymentStats,
} from "../../types/features/administration/Transaction";

export const paymentsService = {
  async getPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_all_payment`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit || undefined,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getPendingPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_pending_payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getSucceededPayoutPending(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_succeeded_payout_pending`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getSucceededPayoutFailed(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_succeeded_payout_failed`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getFailedPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_failed_payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getSucceededPayoutSuccess(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_succeeded_payout_success`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getTodayPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_today_payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getPaymentsByDate(
    token: string,
    date: string,
    pagination?: PaginationParams
  ): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_payments_by_date`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          date_param: date,
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async retryFailedPayment(token: string, paymentId: string): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/payment/send_money_to_failed_payment`,
        { payment_id: paymentId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async changePaymentStatus(
    token: string,
    paymentId: string,
    payoutStatus: string,
    transactionStatus: string
  ): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/change_status_payment/${paymentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          payout_status: payoutStatus,
          transaction_status: transactionStatus,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getUserPayments(
    token: string,
    userId: string,
    pagination?: PaginationParams
  ): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/history_of_user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          user_id: userId,
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getPaymentStatsByCategory(token: string, category: string, date?: string): Promise<PaymentStats> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/stats/by_category`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          category,
          date_param: date || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },
  async getVerificationSides(
    token: string,
    paymentId: string
  ): Promise<{ key: string; label: string; country_code?: string }[]> {
    const response = await axios.get(`${BACKEND_URL}/payment/verification_sides/${paymentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.data?.sides ?? [];
  },
  async verifyOperatorStatus(token: string, paymentId: string, side: string): Promise<any> {
    const response = await axios.get(`${BACKEND_URL}/payment/verify_operator_status/${paymentId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { side },
    });
    return response.data?.data ?? response.data;
  },
};
